import { useEffect, useState, type RefObject } from 'react'

/**
 * Move focus deliberately after an async step, and consume the request.
 *
 * **The fault this exists for.** Every control here disables itself while in
 * flight, and a browser blurs a disabled element to `<body>` — so a keyboard or
 * screen-reader user is returned to the top of the document at exactly the
 * moment something changed. Any step that unmounts the control that was focused
 * does the same. jsdom sees neither: it does not blur removed nodes and does not
 * blur disabled ones, so a suite can be entirely green over it.
 *
 * `MfaChallenge` fixed this three times before it stayed fixed — first only on
 * the first successful send, then leaving a flag set that stole focus from a
 * radio group on a later click. The lesson that survived is in the shape here:
 *
 * - a REQUEST, not a flag. It is consumed by the effect and cleared
 *   unconditionally, so it can never survive into a later render and hijack it.
 * - cleared even when the target is gone. A request that cannot be honoured
 *   must still be spent.
 *
 * Shared rather than copied because it now has four call sites, and the last
 * two rounds of review were spent re-finding the same defect in each new one.
 */
export function useFocusRequest<K extends string>(
  targets: Record<K, RefObject<HTMLElement | null>>,
): (target: K) => void {
  const [requested, setRequested] = useState<K | null>(null)

  useEffect(() => {
    if (requested === null) return
    // Cleared FIRST and unconditionally — see the header.
    setRequested(null)
    targets[requested]?.current?.focus()
    // `targets` is a fresh object literal on every render at every call site, so
    // depending on it would re-run this effect forever. The refs inside it are
    // stable, which is the property that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested])

  return setRequested as (target: K) => void
}
